<template>
  <div class="modern-login-container">
    <div class="login-background">
      <div class="gradient-overlay"></div>
      <div class="animated-shapes">
        <div class="shape shape-1"></div>
        <div class="shape shape-2"></div>
        <div class="shape shape-3"></div>
      </div>
    </div>

    <div class="login-wrapper">
      <div class="login-card">
        <div class="logo-section">
          <div class="logo-icon">
            <el-icon :size="48"><School /></el-icon>
          </div>
          <h1 class="welcome-title">مرحباً بك</h1>
          <p class="welcome-subtitle">سجل دخولك للوصول إلى النظام</p>
        </div>

        <el-form 
          ref="loginFormRef" 
          :model="loginForm" 
          :rules="rules" 
          @submit.prevent="handleSubmit"
          class="login-form"
          :disabled="isLoading"
        >
          <el-form-item prop="username">
            <el-input
              v-model="loginForm.username"
              placeholder="اسم المستخدم"
              size="large"
              :prefix-icon="User"
              class="modern-input"
              :disabled="isLoading"
              @keyup.enter="handleSubmit"
            />
          </el-form-item>

          <el-form-item prop="password">
            <el-input
              v-model="loginForm.password"
              type="password"
              placeholder="كلمة المرور"
              size="large"
              :prefix-icon="Lock"
              class="modern-input"
              :disabled="isLoading"
              show-password
              @keyup.enter="handleSubmit"
            />
          </el-form-item>

          <el-alert
            v-if="error"
            :title="error"
            type="error"
            :closable="false"
            show-icon
            class="error-alert"
          />

          <el-button
            type="primary"
            size="large"
            :loading="isLoading"
            @click="handleSubmit"
            class="login-button"
            native-type="submit"
          >
            <span v-if="!isLoading">تسجيل الدخول</span>
            <span v-else>جاري تسجيل الدخول...</span>
          </el-button>
        </el-form>

        <div class="login-footer">
          <p class="footer-text">© 2024 Caledonian. جميع الحقوق محفوظة</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { requestFirebaseNotificationPermission } from '../firebase';
import { mapMutations, mapActions } from "vuex";
import { School, User, Lock } from '@element-plus/icons-vue';
import { ElMessage } from 'element-plus';

const api = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api',
});

export default {
  name: "sign-in",
  components: {
    School,
    User,
    Lock,
  },
  data() {
    return {
      loginForm: {
        username: '',
        password: '',
      },
      rules: {
        username: [
          { required: true, message: 'يرجى إدخال اسم المستخدم', trigger: 'blur' }
        ],
        password: [
          { required: true, message: 'يرجى إدخال كلمة المرور', trigger: 'blur' },
          { min: 6, message: 'كلمة المرور يجب أن تكون 6 أحرف على الأقل', trigger: 'blur' }
        ],
      },
      error: null,
      isLoading: false,
    };
  },
  beforeMount() {
    // Hide all UI elements on login page
    this.$store.commit('HIDE_ALL');
    this.toggleHideConfig();
    // Clear old cache and session data
    this.clearOldSessionData();
  },
  beforeUnmount() {
    // Don't toggle - let the router guard handle showing elements
    this.toggleHideConfig();
  },
  mounted() {
    // Check if user is already logged in
    if (localStorage.getItem('access_token')) {
      this.$router.push({ name: 'Dashboard' }).catch(() => {});
    }
  },
  methods: {
    ...mapMutations(["toggleHideConfig", "SET_AUTH"]),
    ...mapActions(["showAllElements"]),

    /**
     * Clear old session data and cache
     */
    clearOldSessionData() {
      // Clear old flags
      localStorage.removeItem('has_reloaded');
      localStorage.removeItem('isLoggedIn');
      
      // Clear old cache if user is logging in fresh
      if (!localStorage.getItem('access_token')) {
        // Clear any cached data that might interfere
        sessionStorage.clear();
      }
    },

    /**
     * Clear all user data from localStorage
     */
    clearUserData() {
      const keysToKeep = ['language', 'isDarkMode', 'sidenavCollapsed'];
      const allKeys = Object.keys(localStorage);
      
      allKeys.forEach(key => {
        if (!keysToKeep.includes(key)) {
          localStorage.removeItem(key);
        }
      });
    },

    /**
     * Store user data efficiently
     */
    storeUserData(token, user, badges = []) {
      // Clear old data first
      this.clearUserData();
      
      // Store new data in batch
      const userData = {
        access_token: token,
        user_type: user.user_type,
        user_id: user.id,
        username: user.username,
        user: JSON.stringify({
          ...user,
          badges: badges || []
        }),
      };

      // Store all data at once
      Object.entries(userData).forEach(([key, value]) => {
        localStorage.setItem(key === 'user_id' ? 'userId' : key, value);
      });
    },

    /**
     * Handle login submission
     */
    async handleSubmit() {
      // Validate form
      if (!this.$refs.loginFormRef) return;
      
      try {
        await this.$refs.loginFormRef.validate();
      } catch (error) {
        return;
      }

      this.isLoading = true;
      this.error = null;

      try {
        // Request FCM token in parallel (non-blocking)
        const fcmTokenPromise = requestFirebaseNotificationPermission().catch(() => null);

        // Make login request
        const response = await api.post('/login', {
          login: this.loginForm.username,
          password: this.loginForm.password,
          fcm_token: await fcmTokenPromise,
        });

        const { token, user, badges } = response.data;

        if (!token || !user) {
          throw new Error('استجابة غير صحيحة من الخادم');
        }

        // Clear old data and store new data efficiently
        this.storeUserData(token, user, badges);

        // Force show all UI elements BEFORE setting auth
        this.$store.commit('SHOW_ALL');
        
        // Update Vuex store - this will also ensure all elements are shown
        this.SET_AUTH({
          token,
          userType: user.user_type,
          userId: user.id,
          username: user.username,
        });

        // Double check - ensure all elements are shown
        this.$store.commit('SHOW_ALL');
        this.showAllElements();

        // Wait for Vue to update the DOM and state
        await this.$nextTick();
        await this.$nextTick(); // Double nextTick to ensure reactivity
        
        // Show success message
        ElMessage({
          message: `مرحباً بعودتك، ${user.username}!`,
          type: 'success',
          duration: 2000,
        });

        // Small delay to ensure state is fully updated and DOM is ready
        await new Promise(resolve => setTimeout(resolve, 150));

        // Navigate to dashboard without reload
        await this.$router.push({ name: 'Dashboard' });
        
        // Final check after navigation
        await this.$nextTick();
        this.$store.commit('SHOW_ALL');

      } catch (error) {
        console.error('Login error:', error);
        
        // Clear form on error
        this.loginForm.password = '';
        
        // Set error message
        if (error.response?.status === 401) {
          this.error = 'اسم المستخدم أو كلمة المرور غير صحيحة';
        } else if (error.response?.status === 422) {
          this.error = error.response?.data?.message || 'البيانات المدخلة غير صحيحة';
        } else if (error.response?.data?.message) {
          this.error = error.response.data.message;
        } else {
          this.error = 'حدث خطأ أثناء تسجيل الدخول. يرجى المحاولة مرة أخرى';
        }

        ElMessage({
          message: this.error,
          type: 'error',
          duration: 3000,
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.modern-login-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  font-family: 'Cairo', 'Tajawal', sans-serif;
}

.login-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  z-index: 0;
}

.gradient-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.9) 0%, rgba(118, 75, 162, 0.9) 100%);
  z-index: 1;
}

.animated-shapes {
  position: absolute;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 1;
}

.shape {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  animation: float 20s infinite ease-in-out;
}

.shape-1 {
  width: 300px;
  height: 300px;
  top: -150px;
  right: -150px;
  animation-delay: 0s;
}

.shape-2 {
  width: 200px;
  height: 200px;
  bottom: -100px;
  left: -100px;
  animation-delay: 5s;
}

.shape-3 {
  width: 150px;
  height: 150px;
  top: 50%;
  left: 10%;
  animation-delay: 10s;
}

@keyframes float {
  0%, 100% {
    transform: translate(0, 0) rotate(0deg);
    opacity: 0.3;
  }
  33% {
    transform: translate(30px, -30px) rotate(120deg);
    opacity: 0.5;
  }
  66% {
    transform: translate(-20px, 20px) rotate(240deg);
    opacity: 0.4;
  }
}

.login-wrapper {
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 450px;
  padding: 20px;
}

.login-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 48px 40px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.3);
  animation: slideUp 0.5s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.logo-section {
  text-align: center;
  margin-bottom: 40px;
}

.logo-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.4);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 8px 24px rgba(102, 126, 234, 0.4);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 12px 32px rgba(102, 126, 234, 0.6);
  }
}

.welcome-title {
  font-size: 2rem;
  font-weight: 800;
  color: #1a1f35;
  margin: 0 0 8px 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.welcome-subtitle {
  font-size: 0.95rem;
  color: #6b7280;
  margin: 0;
  font-weight: 500;
}

.login-form {
  margin-top: 32px;
}

.modern-input {
  margin-bottom: 20px;
}

.modern-input :deep(.el-input__wrapper) {
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
}

.modern-input :deep(.el-input__wrapper:hover) {
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
}

.modern-input :deep(.el-input__wrapper.is-focus) {
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.3);
}

.error-alert {
  margin-bottom: 20px;
  border-radius: 12px;
}

.login-button {
  width: 100%;
  height: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.4);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin-top: 8px;
}

.login-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.5);
}

.login-button:active {
  transform: translateY(0);
}

.login-button.is-loading {
  opacity: 0.8;
  cursor: not-allowed;
}

.login-footer {
  margin-top: 32px;
  text-align: center;
  padding-top: 24px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.footer-text {
  font-size: 0.85rem;
  color: #9ca3af;
  margin: 0;
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .login-card {
    background: rgba(26, 31, 46, 0.95);
    border-color: rgba(255, 255, 255, 0.1);
  }

  .welcome-title {
    color: #ffffff;
  }

  .welcome-subtitle {
    color: #9ca3af;
  }

  .footer-text {
    color: #6b7280;
  }
}

/* Responsive design */
@media (max-width: 768px) {
  .login-card {
    padding: 36px 28px;
    border-radius: 20px;
  }

  .welcome-title {
    font-size: 1.75rem;
  }

  .logo-icon {
    width: 70px;
    height: 70px;
  }
}

@media (max-width: 480px) {
  .login-wrapper {
    padding: 16px;
  }

  .login-card {
    padding: 32px 24px;
  }

  .welcome-title {
    font-size: 1.5rem;
  }
}

/* Loading animation */
.login-button.is-loading::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 20px;
  height: 20px;
  margin: -10px 0 0 -10px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
